import { useEffect, useMemo } from 'react';
import useNRLazyQuery from '../../../../graphql/useNRLazyQuery';
import {
  GET_JIRA_PROJECT_COMPONENTS,
  GET_JIRA_PROJECT_LABELS,
  GET_JIRA_PROJECT_SPRINTS,
  GET_JIRA_PROJECT_TEAMS,
  GET_JIRA_PROJECT_USERS,
  GET_JIRA_PROJECT_VERSIONS
} from '../../../../graphql/queries/jiraIntegration';
import { JIRA_RPC_OPTION_SOURCE } from './createJiraTicket.constants';

const JIRA_OPTION_LIST_QUERY_OPTIONS = { fetchPolicy: 'network-only' };

/**
 * Space-scoped Jira option lists for dynamic create-ticket fields.
 * network-only: same variables across issue types would otherwise cache-first and skip re-sync.
 */
export function useJiraCreateTicketOptionLists({ spaceId, issueTypeId, issueFields, requiredRpcOptionSources }) {
  const [projectUsers, , , fetchProjectUsers] = useNRLazyQuery({
    query: GET_JIRA_PROJECT_USERS,
    qlObjKey: 'jiraProjectUsers',
    defaultValue: [],
    options: JIRA_OPTION_LIST_QUERY_OPTIONS
  });

  const [projectVersions, , , fetchProjectVersions] = useNRLazyQuery({
    query: GET_JIRA_PROJECT_VERSIONS,
    qlObjKey: 'jiraProjectVersions',
    defaultValue: [],
    options: JIRA_OPTION_LIST_QUERY_OPTIONS
  });

  const [projectComponents, , , fetchProjectComponents] = useNRLazyQuery({
    query: GET_JIRA_PROJECT_COMPONENTS,
    qlObjKey: 'jiraProjectComponents',
    defaultValue: [],
    options: JIRA_OPTION_LIST_QUERY_OPTIONS
  });

  const [projectLabels, , , fetchProjectLabels] = useNRLazyQuery({
    query: GET_JIRA_PROJECT_LABELS,
    qlObjKey: 'jiraProjectLabels',
    defaultValue: [],
    options: JIRA_OPTION_LIST_QUERY_OPTIONS
  });

  const [projectSprints, , , fetchProjectSprints] = useNRLazyQuery({
    query: GET_JIRA_PROJECT_SPRINTS,
    qlObjKey: 'jiraProjectSprints',
    defaultValue: [],
    options: JIRA_OPTION_LIST_QUERY_OPTIONS
  });

  const [projectTeams, , , fetchProjectTeams] = useNRLazyQuery({
    query: GET_JIRA_PROJECT_TEAMS,
    qlObjKey: 'jiraProjectTeams',
    defaultValue: [],
    options: JIRA_OPTION_LIST_QUERY_OPTIONS
  });

  const activeProjectUsers = useMemo(
    () => (projectUsers ?? []).filter(user => user?.active !== false),
    [projectUsers]
  );

  const rpcOptions = useMemo(
    () => ({
      versions: projectVersions,
      components: projectComponents,
      labels: projectLabels,
      users: activeProjectUsers,
      sprints: projectSprints,
      teams: projectTeams
    }),
    [activeProjectUsers, projectComponents, projectLabels, projectSprints, projectTeams, projectVersions]
  );

  useEffect(() => {
    if (!spaceId || !issueTypeId || !issueFields?.length) {
      return;
    }

    fetchProjectUsers({
      variables: {
        args: {
          spaceId,
          query: '',
          maxResults: 50
        }
      }
    });

    if (requiredRpcOptionSources.has(JIRA_RPC_OPTION_SOURCE.VERSIONS)) {
      fetchProjectVersions({
        variables: {
          args: {
            spaceId,
            includeReleased: true,
            includeArchived: false,
            maxResults: 100
          }
        }
      });
    }

    if (requiredRpcOptionSources.has(JIRA_RPC_OPTION_SOURCE.COMPONENTS)) {
      fetchProjectComponents({
        variables: {
          args: {
            spaceId,
            maxResults: 100
          }
        }
      });
    }

    if (requiredRpcOptionSources.has(JIRA_RPC_OPTION_SOURCE.LABELS)) {
      fetchProjectLabels({
        variables: {
          args: {
            spaceId,
            query: '',
            maxResults: 100
          }
        }
      });
    }

    if (requiredRpcOptionSources.has(JIRA_RPC_OPTION_SOURCE.SPRINTS)) {
      fetchProjectSprints({
        variables: {
          args: {
            spaceId,
            query: '',
            includeClosed: false,
            maxResults: 100
          }
        }
      });
    }

    if (requiredRpcOptionSources.has(JIRA_RPC_OPTION_SOURCE.TEAMS)) {
      fetchProjectTeams({
        variables: {
          args: {
            spaceId,
            query: '',
            maxResults: 100
          }
        }
      });
    }
  }, [
    fetchProjectComponents,
    fetchProjectLabels,
    fetchProjectSprints,
    fetchProjectTeams,
    fetchProjectUsers,
    fetchProjectVersions,
    issueFields,
    issueTypeId,
    requiredRpcOptionSources,
    spaceId
  ]);

  return {
    projectUsers: activeProjectUsers,
    rpcOptions
  };
}
